import { Input, type InputProps } from "@/shared/ui/Input";
import { digitsOnly, groupThousands } from "@/shared/lib/money";

// Masked money input — displays the amount grouped with spaces ("10 000") while the value it stores
// and emits stays a plain digit string ("10000"). Wraps the themed <Input> (type=text + inputMode
// numeric, per the 0072 lesson that type=number breaks thousands separators and the empty state).
//
// Controlled: `value` is the raw digit string; `onChange` receives the raw digit string. The caret is
// re-placed after the same number of digits so grouping never throws the cursor to the end.
export function MoneyInput({
  value,
  onChange,
  ...rest
}: {
  value: string;
  onChange: (raw: string) => void;
} & Omit<InputProps, "value" | "onChange" | "type" | "inputMode">) {
  const display = groupThousands(digitsOnly(value));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    const caret = el.selectionStart ?? el.value.length;
    // How many digits sit to the left of the caret right now — the anchor we restore against.
    const digitsBeforeCaret = digitsOnly(el.value.slice(0, caret)).length;
    const raw = digitsOnly(el.value);

    onChange(raw);

    // After React re-renders with the regrouped display, walk the formatted string until we have
    // passed `digitsBeforeCaret` digits and drop the caret there.
    requestAnimationFrame(() => {
      const next = groupThousands(raw);
      let pos = 0;
      let seen = 0;
      while (pos < next.length && seen < digitsBeforeCaret) {
        if (/\d/.test(next[pos])) seen++;
        pos++;
      }
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <Input type="text" inputMode="numeric" value={display} onChange={handleChange} {...rest} />
  );
}
